import { useCallback, useEffect, useSyncExternalStore } from "react";

/**
 * Folded side rails, remembered on this device.
 *
 * `sidebar` is the app's navigation rail (⌘B / Ctrl+B). `rails` is the second
 * rail some pages carry — Flows' categories, Ads Queue's business units — and
 * folds as one (⌘⇧B / Ctrl+Shift+B), so the habit is the same on every page.
 */
export type CollapseKey = "sidebar" | "rails";

const EVENT = "kobo:collapse";
const storageKey = (key: CollapseKey) => `kobo.collapsed.${key}`;

export function readCollapsed(key: CollapseKey): boolean {
  try {
    return window.localStorage.getItem(storageKey(key)) === "1";
  } catch {
    return false;
  }
}

export function setCollapsed(key: CollapseKey, collapsed: boolean): void {
  try {
    window.localStorage.setItem(storageKey(key), collapsed ? "1" : "0");
  } catch {
    // Blocked storage: the fold still works for this session, it just is not remembered.
    memory.set(key, collapsed);
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: key }));
}

const memory = new Map<CollapseKey, boolean>();

function current(key: CollapseKey): boolean {
  return memory.has(key) ? memory.get(key)! : readCollapsed(key);
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(EVENT, onChange);
  // Another window of the app folding its rail folds this one too.
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export function useCollapsed(key: CollapseKey): [boolean, () => void] {
  const collapsed = useSyncExternalStore(subscribe, () => current(key), () => false);
  const toggle = useCallback(() => setCollapsed(key, !current(key)), [key]);
  return [collapsed, toggle];
}

/** True for ⌘B / Ctrl+B (and the Shift variant). Alt is left for the OS. */
export function collapseChord(event: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey">): CollapseKey | null {
  if (event.altKey || !(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "b") return null;
  return event.shiftKey ? "rails" : "sidebar";
}

/** Installs the two shortcuts once, in the app shell. */
export function useCollapseHotkeys(): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const key = collapseChord(event);
      if (!key || event.repeat) return;
      event.preventDefault();
      setCollapsed(key, !current(key));
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
}

/** Shown in tooltips: the shortcut as this platform writes it. */
export function chordLabel(shift: boolean): string {
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  return mac ? (shift ? "⌘⇧B" : "⌘B") : shift ? "Ctrl+Shift+B" : "Ctrl+B";
}
